import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import React from 'react';

import Panel from 'react-bootstrap/lib/Panel';

import {
  faRocket,
  faRuler,
  faList,
  faChartLine,
  faDollarSign,
} from '@fortawesome/free-solid-svg-icons';

import Link from '../../../../../components/Link';
import PageLayout from '../../../../../components/PageLayout';
import EmailTemplates from '../../../../../components/Handbook/EmailTemplates';

import ShadowerLink from '../../../../../components/ShadowerLink';

import Navbar from '../../../../../components/Handbook/Navbar';

import s from '../../HandbookPage.scss';

class MarketingYourselfPage extends React.Component {
  render() {
    return (
      <PageLayout className={s.page}>
        <div className={s.container}>
          <div className={s.navbar}>
            <div className={s.title}>Shadowing Handbook</div>
            <Navbar
              navbarIcon={faRocket}
              navbarSection="Getting Started"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/welcome-to-shadower"
                  >
                    Welcome to Shadower
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/how-shadower-works"
                  >
                    How Shadower Works
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/working-with-businesses"
                  >
                    Working with Businesses
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/shadowing-guidelines"
                  >
                    Shadowing Guidelines
                  </Link>
                </div>
              }
            />

            <Navbar
              navbarIcon={faRuler}
              navbarSection="Planning a Session"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/choosing-a-topic"
                  >
                    Choosing a Topic
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/interactivity-level"
                  >
                    Interactivity Level
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/session-type"
                  >
                    Session Type
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/providing-resources"
                  >
                    Providing Resources
                  </Link>
                </div>
              }
            />

            <Navbar
              navbarIcon={faList}
              navbarSection="Managing a Session"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/managing-a-session/expectations"
                  >
                    Expectations
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/managing-a-session/session-flow"
                  >
                    Session Flow
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/managing-a-session/maximizing-your-time"
                  >
                    Maximizing Your Time
                  </Link>
                </div>
              }
            />

            <Navbar
              navbarIcon={faChartLine}
              navbarSection="Promoting Your Profile"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/promoting-your-profile/how-the-platform-works"
                  >
                    How the Platform Works
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/promoting-your-profile/optimizing-your-profile"
                  >
                    Optimizing Your Profile
                  </Link>
                  <Link
                    className={cx(s.link, s.active)}
                    to="/handbook/promoting-your-profile/marketing-yourself"
                  >
                    Marketing Yourself
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/promoting-your-profile/how-to-build-a-following"
                  >
                    How to Build a Following
                  </Link>
                </div>
              }
              overrideDefault
            />

            <Navbar
              navbarIcon={faDollarSign}
              navbarSection="Earning Revenue"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/earning-revenue/how-payment-works"
                  >
                    How Payment Works
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/earning-revenue/connecting-with-the-community"
                  >
                    Connecting with the Community
                  </Link>
                </div>
              }
            />

            <Link className={s.newProfileLink} to="/user/profiles/new">
              Create a shadowing profile
            </Link>
          </div>

          <div className={s.explanation}>
            <Panel>
              <Panel.Heading>
                <div>Promoting Your Profile</div>
              </Panel.Heading>
              <Panel.Body>
                <div className={s.handbookTitle}>A guide for experts</div>
                <div className={s.subsectionTitle}>Marketing Yourself</div>

                <p>
                  After you have created a shadowing profile, the quickest way
                  to earn income through Shadower is to develop a solid
                  marketing strategy.
                </p>

                <div className={s.contentContainer}>
                  <div>
                    <strong>Who should I market to?</strong>
                  </div>
                  <p>
                    The most reliable method for earning income is to market
                    your profile to your followers. The average user is much
                    more likely to book a second session with you than to book a
                    first, so building <i>and maintaining</i> a network of
                    connections in your local community is essential to earning
                    stable income through Shadower.{' '}
                  </p>
                </div>

                <div className={s.contentContainer}>
                  <div>
                    <strong>Marketing your profile</strong>
                  </div>

                  <p>
                    If you are brand new to shadowing, you can begin marketing
                    yourself by:
                  </p>

                  <ul>
                    <li>
                      Updating your website or blog:
                      <p>
                        Adding a permanent link to your Shadower profile on your
                        website or blog is one of the simplest ways to attract
                        new shadowers. Let them know you are available for
                        shadowing and direct them to your shadowing profile. If
                        you need help in this process, contact us and our
                        technical team can set it up for you in a matter of
                        minutes.
                      </p>
                    </li>

                    <li>
                      Promote your profile on social media:
                      <p>
                        Let your social media followers know that you now offer
                        a brand new service and provide a link to your profile.
                        We recommend including an engaging photo or video of
                        your work, studio or projects to go along with it.
                        Certain social media sites like Reddit, which have
                        subreddits devoted to every topic on earth is a perfect
                        place to start. Below, we have put together a simple
                        template you can complete and post on your social media
                        sites.
                      </p>
                      <EmailTemplates
                        template={
                          <div>
                            <p>Hey everyone!</p>

                            <p>
                              Exciting news! If you or anyone you know is
                              interested in [<span>topic</span>
                              ], I thought you'd like to know that I offer
                              shadowing on a regular basis through Shadower.
                            </p>

                            <p>
                              If you’re not familiar, Shadower is an online
                              platform that helps amateurs and students find
                              experts to shadow in their community in real life.
                              By shadowing me, you can learn [
                              <span>insert your area of expertise</span>]
                              firsthand and receive immediate feedback, advice
                              and instruction.{' '}
                            </p>

                            <p>
                              To get a feel for how it works, my profile can be
                              found at [
                              <span>insert your shadowing profile URL</span>
                              ]. Booking a session will go a long way toward
                              helping me get established on the platform and
                              discovered more easily in the future.
                            </p>

                            <p>Thanks so much!</p>
                          </div>
                        }
                        viewTemplate="View template"
                      />
                    </li>

                    <li>
                      Reach out to local high schools and colleges:
                      <p>
                        Your influence and knowledge can have an enormous impact
                        on a student searching for their future career path.
                        Consequently, you might find that students provide the
                        greatest opportunity to acquire a solid foundation of
                        regular shadowers. Below, we have put together a simple
                        email template you can complete and send to various
                        educational institutions or teachers in the area.
                      </p>
                      <EmailTemplates
                        template={
                          <div>
                            <p>
                              Dear [<span>Name</span>
                              ],
                            </p>

                            <p>
                              I know how valuable your time is, so I will get
                              right to the point. I work in the field of [
                              <span>topic</span>] and I thought that your
                              students might find it beneficial to know that I
                              offer shadowing on a regular basis through
                              Shadower.
                            </p>

                            <p>
                              {' '}
                              If you’re not familiar, Shadower is an online
                              platform that helps amateurs and students find
                              experts to shadow in their community in real life.
                              By shadowing me, your students can learn [
                              <span>insert your area of expertise</span>] in a
                              real-world environment. To get a feel for how it
                              works, my profile can be found at [
                              <span>insert your shadowing profile URL</span>
                              ].
                            </p>

                            <p>
                              I genuinely believe that your students can greatly
                              benefit from seeing how [<span>topic</span>] works
                              firsthand and receiving immediate feedback, advice
                              and instruction along the way.
                            </p>

                            <p>
                              Thank you for your time and I hope to hear from
                              you!
                            </p>
                            <p>
                              [<span>Your Name</span>]
                            </p>
                          </div>
                        }
                        viewTemplate="View template"
                      />
                    </li>

                    <li>
                      Grow an email list:
                      <p>
                        Creating an email list of subscribers is an extremely
                        effective method to acquire shadowers. Let them know
                        when you create new sessions or offer new learning
                        opportunities. Offering short-term discounts throughout
                        the year might help you drive new shadowers to your
                        profile.
                      </p>
                    </li>
                  </ul>
                </div>

                <ShadowerLink
                  className={s.nextLink}
                  to="/handbook/promoting-your-profile/how-to-build-a-following"
                >
                  Next {'>'}
                </ShadowerLink>
              </Panel.Body>
            </Panel>
          </div>
        </div>
      </PageLayout>
    );
  }
}

export default withStyles(s)(MarketingYourselfPage);
